import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Card, Input, Button, Tooltip, Modal } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  DownloadOutlined,
  FileImageOutlined,
  FilterOutlined,
  CalendarOutlined,
  UnorderedListOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_BASE_URL, NODE_API_BASE_URL } from "../../config";
import { fetchInit, authHeaders, getSessionToken } from "../utils/session";

const PAGE_SIZE = 10;

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtAmount = (val) =>
  Number(val || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const paymentStatusTag = (status, paidAmount) => {
  const isPaid = (status === "1" || status === 1) && paidAmount !== "0.00";
  return isPaid ? (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        color: "#16a34a",
        background: "#dcfce7",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <CheckCircleOutlined /> Paid
    </span>
  ) : (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        color: "#f59e0b",
        background: "#fef9c3",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <ClockCircleOutlined /> Pending
    </span>
  );
};

const planTypeTag = (type) => {
  if (!type) return "—";
  const t = type.toLowerCase();
  const map = {
    monthly: { label: "Monthly", color: "#2563eb", bg: "#eff6ff" },
    annual: { label: "Annually", color: "#7c3aed", bg: "#ede9fe" },
    annualy: { label: "Annually", color: "#7c3aed", bg: "#ede9fe" },
    annually: { label: "Annually", color: "#7c3aed", bg: "#ede9fe" },
    free_trial: { label: "Free Trial", color: "#0891b2", bg: "#e0f2fe" },
  };
  const cfg = map[t] || { label: type, color: "#64748b", bg: "#f1f5f9" };
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
      }}
    >
      {cfg.label}
    </span>
  );
};

const methodTag = (method) => {
  if (!method) return <span style={{ color: "#94a3b8" }}>—</span>;
  const m = method.toLowerCase();
  const map = {
    cash: { color: "#16a34a", bg: "#dcfce7" },
    card: { color: "#2563eb", bg: "#eff6ff" },
    upi: { color: "#7c3aed", bg: "#ede9fe" },
    bank_transfer: { color: "#0891b2", bg: "#e0f2fe" },
    netbanking: { color: "#0891b2", bg: "#e0f2fe" },
    wallet: { color: "#d97706", bg: "#fef3c7" },
  };
  const cfg = map[m] || { color: "#64748b", bg: "#f1f5f9" };
  return (
    <span
      style={{
        padding: "2px 9px",
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        textTransform: "capitalize",
      }}
    >
      {method.replace(/_/g, " ")}
    </span>
  );
};

const StatCardShimmer = () => (
  <div
    style={{
      background: "#fff",
      borderRadius: 14,
      padding: "20px 24px",
      display: "flex",
      alignItems: "center",
      gap: 16,
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
      flex: 1,
      minWidth: 190,
    }}
  >
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 12,
        background:
          "linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.4s infinite",
        flexShrink: 0,
      }}
    />
    <div style={{ flex: 1 }}>
      <div
        style={{
          height: 13,
          width: "60%",
          borderRadius: 6,
          background:
            "linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.4s infinite",
          marginBottom: 10,
        }}
      />
      <div
        style={{
          height: 22,
          width: "80%",
          borderRadius: 6,
          background:
            "linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.4s infinite",
        }}
      />
    </div>
  </div>
);

const statCard = (icon, label, value, color, bg, sub) => (
  <div
    style={{
      background: bg,
      borderRadius: 14,
      padding: "20px 24px",
      display: "flex",
      alignItems: "center",
      gap: 16,
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
      flex: 1,
      minWidth: 190,
    }}
  >
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 12,
        background: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 22,
        color: "#fff",
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 2 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: "#1e293b",
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const PaymentList = () => {
  const token = getSessionToken();
  const { state: locationState } = useLocation();

  // ── Scroll / infinite scroll ──
  const scrollContainerRef = useRef(null);
  const throttleRef = useRef(false);
  const pageRef = useRef(1);

  // ── Table state ──
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  // ── Filters ──
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(locationState?.fromDate ?? "");
  const [toDate, setToDate] = useState(locationState?.toDate ?? "");
  const [paymentStatus, setPaymentStatus] = useState("");
  /* Both arrive from a dashboard chart and are asked of the API, so the list
     and its count describe the same set of rows rather than a page of them. */
  const [paymentMode, setPaymentMode] = useState(locationState?.paymentMode ?? "");
  const [userType, setUserType] = useState(locationState?.userType ?? "");
  const [planId, setPlanId] = useState("");
  const [isRenewed, setIsRenewed] = useState("");

  // ── Plans dropdown ──
  const [plans, setPlans] = useState([]);

  // ── Stats ──
  const [stats, setStats] = useState({
    totalRevenue: 0,
    paidCount: 0,
    pendingCount: 0,
    todayRevenue: 0,
    todayCount: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [exportMode, setExportMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // ── Customer transaction modal ──
  const [custModal, setCustModal] = useState(null); // { user_id, name, email }
  const [custTxns, setCustTxns] = useState([]);
  const [custSummary, setCustSummary] = useState(null);
  const [custLoading, setCustLoading] = useState(false);

  const handleDownloadInvoice = async (url, invoiceId) => {
    try {
      const res = await fetch(url, fetchInit());
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `invoice-${invoiceId || "download"}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      // silent fail
    }
  };

  const openCustomerTxns = async (customer) => {
    setCustModal(customer);
    setCustTxns([]);
    setCustSummary(null);
    setCustLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/subscription/get-customer-transactions`, {
        params: { user_id: customer.user_id, page: 1, limit: 100 },
        headers: { ...authHeaders() },
      });
      if (res.data?.success) {
        setCustTxns(res.data.data || []);
        setCustSummary(res.data.summary || null);
      }
    } catch {
      setCustTxns([]);
    } finally {
      setCustLoading(false);
    }
  };

  // ── Debounce timer ──
  const debounceTimer = useRef(null);

  const toggleExportMode = () => {
    setExportMode((prev) => !prev);
    setSelectedIds([]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === payments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(payments.map((p) => p.payment_id ?? p.invoice_id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleExportCSV = () => {
    const toExport =
      selectedIds.length > 0
        ? payments.filter((p) =>
            selectedIds.includes(p.payment_id ?? p.invoice_id),
          )
        : payments;

    const headers = [
      "Invoice ID",
      "Customer Name",
      "Email",
      "Mobile",
      "Plan Name",
      "Plan Type",
      "Paid Amount",
      "Payment Mode",
      "Payment Date",
      "Payment Status",
      "Renewed",
      "UTR",
      "Remarks",
    ];

    const rows = toExport.map((p) => [
      p.invoice_id || "",
      p.customer_name || "",
      p.customer_email || "",
      p.mobile || "",
      p.plan_name || "",
      p.plan_type || "",
      p.paid_amount || "0.00",
      p.payment_mode || "",
      p.payment_date || "",
      p.payment_status === "1"
        ? "Paid"
        : p.plan_type?.toLowerCase() === "free_trial"
          ? "Free Trial"
          : "Pending",
      p.is_renewed === 1 ? "Yes" : "No",
      p.utr || "",
      p.remarks || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payments_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMode(false);
    setSelectedIds([]);
  };

  // ── Fetch plans from /get-all-plans ──
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/get-all-plans-v1`,
        {headers: { ...authHeaders() }}
      )
      .then((res) => {
        if (res.data?.status === "success") setPlans(res.data.data || []);
      })
      .catch(() => {});
  }, []);


  // ── Fetch a page ──
  const fetchPage = useCallback(
    async (page, isReset = false) => {
      if (isReset) setLoading(true);
      else setIsLoadingMore(true);

      try {
        const res = await axios.get(`${NODE_API_BASE_URL}/api/subscription/get-payment-list`, {
          params: {
            page,
            // limit: PAGE_SIZE,
            limit: 9999,
            search: search || undefined,
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
            payment_status:
              paymentStatus !== "" ? Number(paymentStatus) : undefined,
            payment_mode: paymentMode || undefined,
            user_type: userType || undefined,
            plan_id: planId || undefined,
            is_renewed: isRenewed !== "" ? isRenewed : undefined,
          },
          headers: { ...authHeaders() },
        });

        // console.log("from api", res || []);

        if (res.data?.success || res.data?.status === 200) {
          let newData = res.data.data || [];

          /* The API is asked for the mode above; this re-checks what came back.
             It is a safety net for the window before the param ships, not a
             second filter — once the server honours it, nothing is left to
             remove here. It cannot do the same for user type, which the rows
             do not carry, so that one is the server's alone. */
          if (paymentMode) {
            newData = newData.filter(
              (p) => String(p.payment_mode || "").toLowerCase() === String(paymentMode).toLowerCase(),
            );
          }

          if (paymentStatus === "0") {
            newData = newData.filter(
              (p) =>
                p.payment_status === "0" &&
                p.plan_type?.toLowerCase() !== "free_trial" &&
                p.paid_amount === "0.00",
            );
            setTotal(newData.length);
          } else {
            /* The rows themselves, when a mode filter is in play: the server's
               count is right once it honours the param and wrong before that,
               and this call asks for the whole set either way. */
            setTotal(paymentMode ? newData.length : (res.data.total || 0));
          }

          if (isReset) {
            const paidRecords = newData.filter(
              (p) => (p.payment_status === "1" || p.payment_status === 1) && p.paid_amount !== "0.00",
            );
            const today = new Date().toISOString().split("T")[0];
            const todayDDMMYYYY = today.split("-").reverse().join("/");
            const todayRecords = paidRecords.filter((p) => {
              const d = p.payment_date || "";
              return d.startsWith(today) || d.startsWith(todayDDMMYYYY);
            });
            setStats({
              totalRevenue: res.data.total_revenue ?? 0,
              paidCount: paidRecords.length,
              pendingCount: 0,
              todayRevenue: todayRecords.reduce((s, p) => s + Number(p.paid_amount || 0), 0),
              todayCount: todayRecords.length,
            });
            setStatsLoading(false);
          }

          const lastPage = res.data.last_page || 1;
          setPayments((prev) => (isReset ? newData : [...prev, ...newData]));
          setHasMore(page < lastPage);
        } else {
          if (isReset) setPayments([]);
          setHasMore(false);
        }
      } catch {
        if (isReset) setPayments([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [search, fromDate, toDate, paymentStatus, paymentMode, userType, planId, isRenewed, token],
  );

  // ── Reset and fetch from page 1 ──
  const resetFetch = useCallback(() => {
    pageRef.current = 1;
    setHasMore(true);
    fetchPage(1, true);
  }, [fetchPage]);

  // ── Run reset fetch when filters change ──
  useEffect(() => {
    resetFetch();
  }, [resetFetch]);

  // ── Load more ──
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || loading) return;
    const next = pageRef.current + 1;
    pageRef.current = next;
    fetchPage(next, false);
  }, [isLoadingMore, hasMore, loading, fetchPage]);

  // ── Scroll handler ──
  const handleScroll = useCallback(
    (e) => {
      if (throttleRef.current || !hasMore || isLoadingMore) return;
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
        throttleRef.current = true;
        loadMore();
        setTimeout(() => {
          throttleRef.current = false;
        }, 300);
      }
    },
    [loadMore, hasMore, isLoadingMore],
  );

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // ── Debounced search ──
  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setSearch(val), 500);
  };

  const handleReset = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSearchInput("");
    setSearch("");
    setFromDate("");
    setToDate("");
    setPaymentStatus("");
    setPlanId("");
    setIsRenewed("");
  };

  const inputStyle = {
    height: 36,
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    padding: "0 10px",
    fontSize: 13,
    color: "#374151",
    outline: "none",
    background: "#fff",
    width: "100%",
  };
  const selectStyle = { ...inputStyle, cursor: "pointer" };
  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    marginBottom: 4,
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* ── Header ── */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 18,
                    }}
                  >
                    <CreditCardOutlined />
                  </div>
                  <div>
                    <h4
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        fontSize: 18,
                        color: "#1e293b",
                      }}
                    >
                      Payment List
                    </h4>
                    <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                      All payment transactions across subscriptions
                    </p>
                  </div>
                </div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/cms/home">CMS</Link>
                  </li>
                  <li className="breadcrumb-item active">Payment List</li>
                </ol>
              </div>
            </div>
          </div>

          {/* ── Stats ── */}
          <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              marginBottom: 24,
            }}
          >
            {statsLoading ? (
              <>
                <StatCardShimmer />
                <StatCardShimmer />
                <StatCardShimmer />
                <StatCardShimmer />
              </>
            ) : (
              <>
                {statCard(
                  <DollarOutlined />,
                  "Total Revenue",
                  `₹${fmtAmount(stats.totalRevenue)}`,
                  "linear-gradient(135deg,#6366f1,#4f46e5)",
                  "#eef2ff",
                )}
                {statCard(
                  <CheckCircleOutlined />,
                  "Paid Payments",
                  stats.paidCount,
                  "linear-gradient(135deg,#22c55e,#16a34a)",
                  "#f0fdf4",
                  "Total paid",
                )}
                {statCard(
                  <CalendarOutlined />,
                  "Today's Collections",
                  `₹${fmtAmount(stats.todayRevenue)}`,
                  "linear-gradient(135deg,#0891b2,#0e7490)",
                  "#e0f2fe",
                  `${stats.todayCount} payment${stats.todayCount !== 1 ? "s" : ""} today`,
                )}
              </>
            )}
          </div>

          {/* ── Filters ── */}
          <Card
            style={{
              borderRadius: 14,
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              marginBottom: 20,
            }}
            bodyStyle={{ padding: "16px 20px" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 14,
              }}
            >
              <FilterOutlined style={{ color: "#6366f1" }} />
              <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>
                Filters
              </span>
            </div>
            <div className="row g-3">
              <div className="col-md-3">
                <div style={labelStyle}>Search</div>
                <Input
                  prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                  placeholder="Name, email, invoice..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  allowClear
                  onClear={() => {
                    if (debounceTimer.current)
                      clearTimeout(debounceTimer.current);
                    setSearchInput("");
                    setSearch("");
                  }}
                  style={{ borderRadius: 8, height: 36 }}
                />
              </div>
              <div className="col-md-2">
                <div style={labelStyle}>From Date</div>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div className="col-md-2">
                <div style={labelStyle}>To Date</div>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={inputStyle}
                />
              </div>
              {/* Spelled out because the filter was set by the page that linked
                  here, not by anything on this screen — without it the list is
                  short for no visible reason. */}
              {[
                { label: "Payment Mode", value: paymentMode, clear: () => setPaymentMode("") },
                {
                  label: "User Type",
                  value: userType && (userType === "attorney" ? "Attorney / IP" : "Corporate"),
                  clear: () => setUserType(""),
                },
              ].filter((f) => f.value).map((f) => (
                <div className="col-md-2" key={f.label}>
                  <div style={labelStyle}>{f.label}</div>
                  <button
                    type="button"
                    onClick={f.clear}
                    title="Clear this filter"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8, height: 36,
                      padding: "0 12px", borderRadius: 8, cursor: "pointer",
                      background: "#eef2ff", border: "1px solid #c7d2fe",
                      color: "#4338ca", fontSize: 13, fontWeight: 600,
                    }}
                  >
                    {f.value} <span style={{ fontSize: 15, lineHeight: 1 }}>&times;</span>
                  </button>
                </div>
              ))}
              {/* <div className="col-md-2">
                <div style={labelStyle}>Payment Status</div>
                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">All</option>
                  <option value="1">Paid</option>
                  <option value="0">Pending</option>
                </select>
              </div> */}
              <div className="col-md-2">
                <div style={labelStyle}>Plan</div>
                <select
                  value={planId}
                  onChange={(e) => setPlanId(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">All Plans</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* <div className="col-md-1">
                <div style={labelStyle}>Renewed</div>
                <select
                  value={isRenewed}
                  onChange={(e) => setIsRenewed(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">All</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div> */}
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 14,
                justifyContent: "flex-end",
              }}
            >
              <Button
                icon={<CalendarOutlined />}
                onClick={() => {
                  const today = new Date().toISOString().split("T")[0];
                  setFromDate(today);
                  setToDate(today);
                  setPaymentStatus("1");
                }}
                type={
                  fromDate === toDate &&
                  fromDate === new Date().toISOString().split("T")[0]
                    ? "primary"
                    : "default"
                }
                style={{ borderRadius: 8, height: 36 }}
              >
                Today
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                style={{ borderRadius: 8, height: 36 }}
              >
                Reset
              </Button>
            </div>
          </Card>

          {/* ── Table ── */}
          <Card
            style={{
              borderRadius: 14,
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            }}
            bodyStyle={{ padding: 0 }}
          >
            <div
              style={{
                padding: "14px 20px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>
                Payment Records
              </span>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Export Controls */}
                {exportMode ? (
                  <>
                    <Button
                      onClick={toggleSelectAll}
                      style={{
                        height: 36,
                        borderRadius: 8,
                        border: "1px solid #c7d2fe",
                        background: "#f0f4ff",
                        color: "#6366f1",
                        fontWeight: 600,
                        fontSize: 13,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        paddingInline: 12,
                        boxShadow: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          selectedIds.length === payments.length &&
                          payments.length > 0
                        }
                        onChange={toggleSelectAll}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          cursor: "pointer",
                          width: 14,
                          height: 14,
                          margin: 0,
                        }}
                      />
                      Select All ({payments.length})
                    </Button>
                    <Button
                      onClick={handleExportCSV}
                      icon={<DownloadOutlined />}
                      style={{
                        height: 36,
                        borderRadius: 8,
                        border: "none",
                        background: "linear-gradient(135deg, #22c55e, #16a34a)",
                        color: "white",
                        fontWeight: 600,
                        fontSize: 13,
                        boxShadow: "0 4px 12px rgba(34,197,94,0.3)",
                        paddingInline: 16,
                      }}
                    >
                      Export{" "}
                      {selectedIds.length > 0
                        ? `(${selectedIds.length})`
                        : `All (${payments.length})`}
                    </Button>
                    <Button
                      onClick={toggleExportMode}
                      style={{
                        height: 36,
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        background: "white",
                        color: "#6b7280",
                        fontWeight: 600,
                        fontSize: 13,
                        paddingInline: 14,
                      }}
                    >
                      ✕ Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={toggleExportMode}
                    icon={<DownloadOutlined />}
                    style={{
                      height: 36,
                      borderRadius: 8,
                      border: "none",
                      background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                      color: "white",
                      fontWeight: 600,
                      fontSize: 13,
                      paddingInline: 18,
                      boxShadow: "0 4px 12px rgba(8,145,178,0.3)",
                    }}
                  >
                    Export
                  </Button>
                )}

                <span style={{ color: "#64748b", fontSize: 13 }}>
                  Total: <b>{total}</b> records
                </span>
              </div>
            </div>

            {/* Scrollable table container */}
            <div
              ref={scrollContainerRef}
              style={{
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: "calc(100vh - 380px)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  minWidth: 1100,
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {exportMode && (
                      <th
                        style={{
                          padding: "12px 14px",
                          width: 40,
                          background: "#f8fafc",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            selectedIds.length === payments.length &&
                            payments.length > 0
                          }
                          onChange={toggleSelectAll}
                          style={{ cursor: "pointer", width: 15, height: 15 }}
                        />
                      </th>
                    )}
                    {[
                      "#",
                      "Invoice ID",
                      "Customer",
                      "Plan",
                      "Amount",
                      "Method",
                      "Date",
                      "Status",
                      "Renewed",
                      "Remarks",
                      "Actions",
                    ].map((c) => (
                      <th
                        key={c}
                        style={{
                          padding: "12px 14px",
                          textAlign: "left",
                          fontWeight: 600,
                          color: "#475569",
                          fontSize: 12,
                          borderBottom: "1px solid #e2e8f0",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          background: "#f8fafc",
                          zIndex: 1,
                        }}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={12}
                        style={{
                          textAlign: "center",
                          padding: 48,
                          color: "#94a3b8",
                        }}
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : payments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={12}
                        style={{ textAlign: "center", padding: 48 }}
                      >
                        <div style={{ color: "#94a3b8", fontSize: 15 }}>
                          <CreditCardOutlined
                            style={{
                              fontSize: 36,
                              marginBottom: 12,
                              display: "block",
                            }}
                          />
                          No payment records found
                        </div>
                      </td>
                    </tr>
                  ) : (
                    payments.map((p, idx) => (
                      <tr
                        key={`${p.payment_id ?? "null"}-${idx}`}
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f8fafc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "")
                        }
                      >
                        {exportMode && (
                          <td
                            style={{
                              padding: "12px 14px",
                              textAlign: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(
                                p.payment_id ?? p.invoice_id,
                              )}
                              onChange={() =>
                                toggleSelectOne(p.payment_id ?? p.invoice_id)
                              }
                              style={{
                                cursor: "pointer",
                                width: 15,
                                height: 15,
                              }}
                            />
                          </td>
                        )}
                        <td style={{ padding: "12px 14px", color: "#64748b" }}>
                          {idx + 1}
                        </td>

                        <td style={{ padding: "12px 14px" }}>
                          {p.invoice_id ? (
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontSize: 11,
                                color: "#6366f1",
                                fontWeight: 600,
                                background: "#eef2ff",
                                padding: "2px 7px",
                                borderRadius: 5,
                              }}
                            >
                              {p.invoice_id}
                            </span>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: 12 }}>
                              —
                            </span>
                          )}
                        </td>

                        <td style={{ padding: "12px 14px" }}>
                          <div
                            onClick={() => p.user_id && openCustomerTxns({ user_id: p.user_id, name: p.customer_name, email: p.customer_email })}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              cursor: p.user_id ? "pointer" : "default",
                              borderRadius: 8,
                              padding: "4px 6px",
                              margin: "-4px -6px",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => { if (p.user_id) e.currentTarget.style.background = "#f0f4ff"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                          >
                            <div
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: 12,
                                flexShrink: 0,
                              }}
                            >
                              {(p.customer_name || "U")[0]?.toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, color: "#6366f1", fontSize: 13 }}>
                                {p.customer_name || "—"}
                              </div>
                              <div style={{ fontSize: 11, color: "#94a3b8" }}>
                                {p.customer_email || ""}
                              </div>
                              <div style={{ fontSize: 11, color: "#94a3b8" }}>
                                {p.mobile || ""}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                          <div
                            style={{
                              fontWeight: 600,
                              color: "#1e293b",
                              fontSize: 13,
                              marginBottom: 3,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {p.plan_name || "—"}
                          </div>
                          {planTypeTag(p.plan_type)}
                        </td>

                        <td style={{ padding: "12px 14px" }}>
                          <span
                            style={{
                              fontWeight: 700,
                              color: "#16a34a",
                              fontSize: 14,
                            }}
                          >
                            ₹{fmtAmount(p.paid_amount)}
                          </span>
                          {p.gst_inclusion === 1 && (
                            <div style={{ fontSize: 10, color: "#94a3b8" }}>
                              incl. GST
                            </div>
                          )}
                        </td>

                        <td style={{ padding: "12px 14px" }}>
                          {methodTag(p.payment_mode)}
                          {p.utr && (
                            <Tooltip title={`UTR: ${p.utr}`}>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: "#94a3b8",
                                  marginTop: 2,
                                  cursor: "help",
                                }}
                              >
                                UTR: {String(p.utr).slice(0, 8)}…
                              </div>
                            </Tooltip>
                          )}
                        </td>

                        <td
                          style={{
                            padding: "12px 14px",
                            color: "#475569",
                            fontSize: 12,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {p.payment_date || "—"}
                        </td>

                        <td style={{ padding: "12px 14px" }}>
                          {p.plan_type?.toLowerCase() === "free_trial" ? (
                            <span
                              style={{
                                padding: "3px 10px",
                                borderRadius: 20,
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#0891b2",
                                background: "#e0f2fe",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              Free Trial
                            </span>
                          ) : (
                            paymentStatusTag(p.payment_status, p.paid_amount)
                          )}
                        </td>

                        <td
                          style={{ padding: "12px 14px", textAlign: "center" }}
                        >
                          {p.is_renewed === 1 ? (
                            <span
                              style={{
                                fontSize: 11,
                                color: "#16a34a",
                                background: "#dcfce7",
                                padding: "2px 8px",
                                borderRadius: 10,
                                fontWeight: 600,
                              }}
                            >
                              Yes
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: 11,
                                color: "#64748b",
                                background: "#f1f5f9",
                                padding: "2px 8px",
                                borderRadius: 10,
                                fontWeight: 600,
                              }}
                            >
                              No
                            </span>
                          )}
                        </td>

                        <td style={{ padding: "12px 14px", maxWidth: 160 }}>
                          {p.remarks ? (
                            <Tooltip title={p.remarks}>
                              <span
                                style={{
                                  fontSize: 12,
                                  color: "#475569",
                                  overflow: "hidden",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {p.remarks}
                              </span>
                            </Tooltip>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>—</span>
                          )}
                        </td>

                        <td style={{ padding: "12px 14px" }}>
                          <div style={{ display: "flex", gap: 6 }}>
                            {p.invoice_url &&
                            p.plan_type?.toLowerCase() !== "free_trial" ? (
                              <>
                                <Tooltip title="Preview Invoice">
                                  <span
                                    onClick={() => window.open(p.invoice_url, "_blank")}
                                    style={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: 6,
                                      border: "1px solid #bbf7d0",
                                      background: "#f0fdf4",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#16a34a",
                                      fontSize: 13,
                                      cursor: "pointer",
                                    }}
                                  >
                                    <EyeOutlined />
                                  </span>
                                </Tooltip>
                                <Tooltip title="Download Invoice">
                                  <span
                                    onClick={() => handleDownloadInvoice(p.invoice_url, p.invoice_id)}
                                    style={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: 6,
                                      border: "1px solid #e0e7ff",
                                      background: "#eef2ff",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#6366f1",
                                      fontSize: 13,
                                      cursor: "pointer",
                                    }}
                                  >
                                    <DownloadOutlined />
                                  </span>
                                </Tooltip>
                              </>
                            ) : p.plan_type?.toLowerCase() === "free_trial" ? (
                              <Tooltip title="Invoice not available for Free Trial">
                                <span
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 6,
                                    border: "1px solid #e2e8f0",
                                    background: "#f8fafc",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#cbd5e1",
                                    fontSize: 13,
                                    cursor: "not-allowed",
                                  }}
                                >
                                  <DownloadOutlined />
                                </span>
                              </Tooltip>
                            ) : null}
                            {p.payment_proof_url && (
                              <Tooltip title="View Payment Proof">
                                <a
                                  href={p.payment_proof_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 6,
                                    border: "1px solid #dcfce7",
                                    background: "#f0fdf4",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#16a34a",
                                    fontSize: 13,
                                  }}
                                >
                                  <FileImageOutlined />
                                </a>
                              </Tooltip>
                            )}
                            {p.user_id && (
                              <Tooltip title="View All Transactions">
                                <span
                                  onClick={() => openCustomerTxns({ user_id: p.user_id, name: p.customer_name, email: p.customer_email })}
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 6,
                                    border: "1px solid #e0e7ff",
                                    background: "#eef2ff",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#6366f1",
                                    fontSize: 13,
                                    cursor: "pointer",
                                  }}
                                >
                                  <UnorderedListOutlined />
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Infinite scroll indicators */}
              {isLoadingMore && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    color: "#9ca3af",
                  }}
                >
                  <div
                    className="spinner-border spinner-border-sm"
                    style={{ color: "#6366f1", marginRight: 8 }}
                    role="status"
                  />
                  <span style={{ fontSize: 13 }}>Loading more...</span>
                </div>
              )}
              {!hasMore && payments.length > 0 && !loading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "14px",
                    color: "#cbd5e1",
                    fontSize: 12,
                  }}
                >
                  All records loaded
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* ── Customer Transaction History Modal ── */}
      <Modal
        open={!!custModal}
        onCancel={() => setCustModal(null)}
        footer={null}
        width={820}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#6366f1,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 14 }}>
              {(custModal?.name || "U")[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>{custModal?.name}</div>
              <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>{custModal?.email}</div>
            </div>
          </div>
        }
        styles={{ body: { padding: "16px 0 0" } }}
      >
        {/* Summary cards */}
        {custSummary && (
          <div style={{ display: "flex", gap: 12, padding: "0 24px 16px", flexWrap: "wrap" }}>
            {[
              { label: "Total Paid", val: `₹${Number(custSummary.total_paid || 0).toLocaleString("en-IN")}`, color: "#16a34a", bg: "#f0fdf4" },
              { label: "Total Transactions", val: custSummary.total_transactions ?? 0, color: "#6366f1", bg: "#eef2ff" },
              { label: "First Payment", val: custTxns[custTxns.length - 1]?.transaction_at?.split(" ")[0] || "—", color: "#0891b2", bg: "#e0f2fe" },
              { label: "Last Payment", val: custTxns[0]?.transaction_at?.split(" ")[0] || "—", color: "#d97706", bg: "#fef3c7" },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, minWidth: 140, background: s.bg, borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 3 }}>{s.label}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: s.color }}>{s.val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Transaction table */}
        <div style={{ maxHeight: 400, overflowY: "auto" }}>
          {custLoading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>
              <div className="spinner-border spinner-border-sm" style={{ color: "#6366f1", marginRight: 8 }} role="status" />
              Loading transactions...
            </div>
          ) : custTxns.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>No transactions found</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["#", "Invoice ID", "Plan", "Amount", "Mode", "Date", "Status", "Invoice"].map((h) => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 12, borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {custTxns.map((t, i) => (
                  <tr key={t.transaction_id ?? i} style={{ borderBottom: "1px solid #f1f5f9" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                    <td style={{ padding: "10px 14px", color: "#94a3b8" }}>{i + 1}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontFamily: "monospace", fontSize: 11, color: "#6366f1", fontWeight: 600, background: "#eef2ff", padding: "2px 6px", borderRadius: 4 }}>{t.invoice_id || "—"}</span>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 12 }}>{t.plan_name || "—"}</div>
                      {planTypeTag(t.plan_type)}
                    </td>
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: "#16a34a" }}>₹{fmtAmount(t.paid_amount)}</td>
                    <td style={{ padding: "10px 14px" }}>{methodTag(t.payment_mode)}</td>
                    <td style={{ padding: "10px 14px", color: "#475569", fontSize: 12, whiteSpace: "nowrap" }}>{t.transaction_at || "—"}</td>
                    <td style={{ padding: "10px 14px" }}>{paymentStatusTag(t.payment_status, t.paid_amount)}</td>
                    <td style={{ padding: "10px 14px" }}>
                      {t.invoice_url ? (
                        <div style={{ display: "flex", gap: 5 }}>
                          <Tooltip title="Preview Invoice">
                            <span
                              onClick={() => window.open(t.invoice_url, "_blank")}
                              style={{ width: 26, height: 26, borderRadius: 6, border: "1px solid #bbf7d0", background: "#f0fdf4", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#16a34a", fontSize: 12, cursor: "pointer" }}>
                              <EyeOutlined />
                            </span>
                          </Tooltip>
                          <Tooltip title="Download Invoice">
                            <span
                              onClick={() => handleDownloadInvoice(t.invoice_url, t.invoice_id)}
                              style={{ width: 26, height: 26, borderRadius: 6, border: "1px solid #e0e7ff", background: "#eef2ff", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#6366f1", fontSize: 12, cursor: "pointer" }}>
                              <DownloadOutlined />
                            </span>
                          </Tooltip>
                        </div>
                      ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PaymentList;
